import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { value: 'search_topics', label: 'Search Topics', description: 'Q&A topic frequency analysis' },
  { value: 'issue_summary', label: 'Issue Summary', description: 'Member issue reports by type' },
  { value: 'location_summary', label: 'Location Summary', description: 'Issues grouped by location' },
  { value: 'monthly_summary', label: 'Monthly Summary', description: 'Combined monthly trends' }
];

function AdminAnalyticsNew({ user }) {
  const navigate = useNavigate();
  
  const [filters, setFilters] = useState({
    report_type: 'search_topics',
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    topic: '',
    issue_type: '',
    location: ''
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear report when filters change
    setReportData(null);
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.append('report_type', filters.report_type);
    params.append('start_date', filters.start_date);
    params.append('end_date', filters.end_date);
    if (filters.topic) params.append('topic', filters.topic);
    if (filters.issue_type) params.append('issue_type', filters.issue_type);
    if (filters.location) params.append('location', filters.location);
    return params.toString();
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setError('');
    setReportData(null);

    try {
      const token = localStorage.getItem('token');
      const queryString = buildQueryString();
      const res = await axios.get(
        `${API}/api/ask-stewie/analytics/report?${queryString}&format=json`,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Is-Admin': 'true'
          },
        }
      );
      setReportData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryString = buildQueryString();
      const response = await axios.get(
        `${API}/api/ask-stewie/analytics/report?${queryString}&format=csv`,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Is-Admin': 'true'
          },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filters.report_type}_${filters.start_date}_${filters.end_date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to download CSV');
    }
  };

  const renderReportTable = () => {
    if (!reportData || !reportData.rows || reportData.rows.length === 0) {
      return (
        <div className="empty-report">
          <div className="empty-icon">📊</div>
          <p>No data found for the selected filters</p>
        </div>
      );
    }

    const columns = Object.keys(reportData.rows[0]);

    return (
      <div className="report-table-container">
        <table className="report-table" data-testid="report-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col}>{col.replace(/_/g, ' ').toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.rows.map((row, idx) => (
              <tr key={idx}>
                {columns.map(col => (
                  <td key={col}>{row[col]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const getSelectedReportType = () => {
    return REPORT_TYPES.find(rt => rt.value === filters.report_type);
  };

  return (
    <div className="as-analytics-page admin-analytics" data-testid="admin-analytics-page">
      <header className="as-header">
        <div className="as-badge">Admin Only</div>
        <h1>Analytics Dashboard</h1>
        <p>Generate reports on search trends and member issues.</p>
      </header>

      <div className="as-card">
        <div className="card-header">
          <h2>Report Generator</h2>
          <p>Select report type and date range to generate analytics</p>
        </div>
        <div className="card-body">
          <div className="report-type-selector">
            {REPORT_TYPES.map(rt => (
              <label 
                key={rt.value} 
                className={`report-type-option ${filters.report_type === rt.value ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="report_type"
                  value={rt.value}
                  checked={filters.report_type === rt.value}
                  onChange={handleFilterChange}
                  data-testid={`report-type-${rt.value}`}
                />
                <div className="option-content">
                  <span className="option-label">{rt.label}</span>
                  <span className="option-desc">{rt.description}</span>
                </div>
              </label>
            ))}
          </div>

          <div className="filters-section">
            <h3>Filters</h3>
            <div className="field-row">
              <div className="field-group half">
                <label>Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                  className="text-input"
                  data-testid="start-date"
                />
              </div>
              <div className="field-group half">
                <label>End Date</label>
                <input
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                  className="text-input"
                  data-testid="end-date"
                />
              </div>
            </div>

            {(filters.report_type === 'search_topics') && (
              <div className="field-group">
                <label>Filter by Topic (Optional)</label>
                <input
                  type="text"
                  name="topic"
                  value={filters.topic}
                  onChange={handleFilterChange}
                  placeholder="e.g., overtime, discipline"
                  className="text-input"
                  data-testid="topic-filter"
                />
              </div>
            )}

            {(filters.report_type === 'issue_summary' || filters.report_type === 'location_summary') && (
              <div className="field-row">
                <div className="field-group half">
                  <label>Filter by Issue Type (Optional)</label>
                  <input
                    type="text"
                    name="issue_type"
                    value={filters.issue_type}
                    onChange={handleFilterChange}
                    placeholder="e.g., Safety Concern"
                    className="text-input"
                    data-testid="issue-type-filter"
                  />
                </div>
                <div className="field-group half">
                  <label>Filter by Location (Optional)</label>
                  <input
                    type="text"
                    name="location"
                    value={filters.location}
                    onChange={handleFilterChange}
                    placeholder="e.g., Building A"
                    className="text-input"
                    data-testid="location-filter"
                  />
                </div>
              </div>
            )}
          </div>

          {error && <div className="error-message" data-testid="error">{error}</div>}

          <div className="button-row">
            <button 
              className="btn-primary" 
              onClick={handleGenerateReport}
              disabled={loading}
              data-testid="generate-report-btn"
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
            {reportData && reportData.rows?.length > 0 && (
              <button 
                className="btn-secondary"
                onClick={handleDownloadCSV}
                data-testid="download-csv-btn"
              >
                📥 Download CSV
              </button>
            )}
          </div>

          {reportData && (
            <div className="report-results" data-testid="report-results">
              <div className="results-header">
                <h3>{getSelectedReportType()?.label}</h3>
                <div className="results-meta">
                  <span>{reportData.start_date} to {reportData.end_date}</span>
                  <span className="badge">{reportData.row_count} rows</span>
                </div>
              </div>
              {renderReportTable()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAnalyticsNew;
